import { useState, useMemo, useEffect } from "react";
import { Box, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogActions, Stack, Typography } from "@mui/material";
import { CalendarEvent, CalendarServiceModule } from "../../../types/serviceModuleTypes";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import SelectGroupsList from "../../../components/groups/SelectGroupsList";
import ReminderNotificationsSelector from "./ReminderNotificationsSelector";
import { useTranslation } from "react-i18next";
import utc from 'dayjs/plugin/utc'
import { useGroupsData } from "../../../store/selectors/groupSelectors";
import IconAndColorPicker from "../../../common/ui/IconAndColorPicker";
import CalendarEventSurveyDetails from "./CalendarEventSurveyDetails";
import TranslatableTextField from "../../../components/common/translations/TranslatableTextField";
import { GeneralButton } from "../../../common/ui/GeneralButton";

dayjs.extend(utc);

interface CalendarEventModalProps {
    open: boolean;
    handleClose: () => void;
    serviceModule: CalendarServiceModule;
    event: Partial<CalendarEvent>;
    onSave: (eventData: Partial<CalendarEvent>) => void;
    onDelete: (eventId: number) => void;
    onCopy?: (eventData: CalendarEvent) => void;
}

const CalendarEventModal = ({ open, handleClose, serviceModule, event, onSave, onDelete, onCopy }: CalendarEventModalProps) => {
    const { t } = useTranslation();
    const isNewEvent = !event.id;

    const initialEventState = useMemo(() => {
        const startDate = event.startDate ? dayjs(event.startDate) : dayjs();
        const endDate = event.endDate ? dayjs(event.endDate) : startDate;

        return {
            title: event.title ?? "",
            description: event.description ?? "",
            capacity: event.capacity,
            startDate: startDate,
            startTime: startDate,
            endDate: endDate,
            endTime: endDate,
            hasTime: event.hasTime === true,
            hasEndDate: event.startDate !== event.endDate,
            iconName: event.iconName ?? '',
            colorHex: event.colorHex ?? '',
            iconUrl: event.iconUrl ?? null,
            isCompanyWideAssigned: event.isCompanyWideAssigned === true,
            assignedGroupIds: event.assignedGroupIds ?? [],
            reminderNotifications: event.reminderNotifications ?? [],
            sendUpdateNotification: event.sendUpdateNotification ?? false,
            surveyAppointmentAllowed: event.surveyAppointmentAllowed,
            surveyAppointmentReward: event.surveyAppointmentReward,
        };
    }, [event]);

    const [formData, setFormData] = useState(initialEventState);
    const isSaveDisabled = formData.title.trim() === "";
    const groupsData = useGroupsData();
    const selectedGroups = groupsData
        .filter(group => formData.assignedGroupIds.includes(group.id))
        .map(group => group.name)
        .join(", ");

    const assignmentLabel = formData.isCompanyWideAssigned
        ? t("serviceModulesPage.companyWideAssigned")
        : `${t("serviceModulesPage.visibleForGroups")}: ${selectedGroups || t("serviceModulesPage.noGroupsSelected")}`;

    useEffect(() => {
        if (open) {
            setFormData(initialEventState);
        }
    }, [open, event]);

    const handleSave = () => {
        const start = formData.hasTime
            ? formData.startDate.hour(formData.startTime.hour()).minute(formData.startTime.minute()).second(0)
            : formData.startDate.startOf("day");

        const end = formData.hasEndDate
            ? formData.hasTime
                ? formData.endDate.hour(formData.endTime.hour()).minute(formData.endTime.minute()).second(0)
                : formData.endDate.startOf("day")
            : start;

        onSave({
            ...event,
            title: formData.title,
            description: formData.description,
            iconName: formData.iconName,
            colorHex: formData.colorHex,
            iconUrl: formData.iconUrl,
            startDate: start.utc(true).toDate().toISOString(),
            endDate: end.utc(true).toDate().toISOString(),
            hasTime: formData.hasTime,
            assignedGroupIds: formData.assignedGroupIds,
            isCompanyWideAssigned: formData.isCompanyWideAssigned,
            reminderNotifications: formData.reminderNotifications,
            sendUpdateNotification: formData.sendUpdateNotification,
            capacity: formData.capacity,
            surveyAppointmentAllowed: formData.surveyAppointmentAllowed,
            surveyAppointmentReward: formData.surveyAppointmentReward
        });
    }

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
            <DialogTitle>
                {isNewEvent ? t("serviceModulesPage.calendarPage.createEvent") : t("serviceModulesPage.calendarPage.editEvent")}
            </DialogTitle>
            <Stack direction="row" sx={{ m: 3, mt: 0 }} gap={4}>
                <Box
                    gap={1}
                    flex={1}
                    display="flex"
                    width="500px"
                    flexDirection="column" alignItems="stretch">

                    <Typography variant="subtitle2">{t("serviceModulesPage.calendarPage.eventDetails")}</Typography>
                    <IconAndColorPicker
                        parentId={serviceModule.id}
                        parentType="calendarEvent"
                        eventId={event.id ?? 0}
                        uploadDisabled={!event.id}
                        iconData={{
                            colorHex: formData.colorHex,
                            iconName: formData.iconName,
                            iconUrl: formData.iconUrl
                        }}
                        onChange={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
                    />

                    <TranslatableTextField
                        label={t("serviceModulesPage.calendarPage.eventTitle")}
                        value={formData.title}
                        onChange={(newVal) => setFormData({ ...formData, title: newVal })}
                        entityType="calendarEvent"
                        entityId={String(event.id ?? 0)}
                        property="title"
                        textFieldProps={{
                            required: true,
                            error: formData.title.trim() === ""
                        }}
                    />
                    <Stack direction="column" gap={1}>
                        <TranslatableTextField
                            label={t("serviceModulesPage.calendarPage.eventDescription")}
                            value={formData.description}
                            onChange={(newVal) => setFormData({ ...formData, description: newVal })}
                            entityType="calendarEvent"
                            entityId={String(event.id ?? 0)}
                            property="description"
                            textFieldProps={{
                                multiline: true,
                                rows: 2,
                                sx: { flex: 1, mb: 3 }
                            }}
                        />
                    </Stack>

                    <Stack direction="row" gap={2}>
                        <DatePicker
                            value={formData.startDate}
                            sx={{ flex: 1 }}
                            format="DD.M.YYYY"
                            maxDate={formData.hasEndDate ? formData.endDate : undefined}
                            onChange={(v) => v && setFormData({ ...formData, startDate: v })}
                            label={t("serviceModulesPage.calendarPage.eventStartDate")}
                        />
                        {formData.hasTime && (
                            <TimePicker
                                value={formData.startTime}
                                sx={{ flex: 1 }}
                                ampm={false}
                                format="HH:mm"
                                onChange={(v) => v && setFormData({ ...formData, startTime: v })}
                                label={t("serviceModulesPage.calendarPage.eventStartTime")}
                            />
                        )}
                    </Stack>

                    {formData.hasEndDate && (
                        <Stack direction="row" gap={2} sx={{ mt: 2 }}>
                            <DatePicker
                                value={formData.endDate}
                                sx={{ flex: 1 }}
                                format="DD.M.YYYY"
                                minDate={formData.startDate}
                                onChange={(v) => v && setFormData({ ...formData, endDate: v })}
                                label={t("serviceModulesPage.calendarPage.eventEndDate")}
                            />
                            {formData.hasTime && (
                                <TimePicker
                                    value={formData.endTime}
                                    sx={{ flex: 1 }}
                                    ampm={false}
                                    format="HH:mm"
                                    onChange={(v) => v && setFormData({ ...formData, endTime: v })}
                                    label={t("serviceModulesPage.calendarPage.eventEndTime")}
                                />
                            )}
                        </Stack>
                    )}

                    <Box>
                        <FormControlLabel
                            label={t("serviceModulesPage.calendarPage.eventAlternativeEnd")}
                            control={<Checkbox
                                checked={formData.hasEndDate}
                                onChange={(v) => setFormData({
                                    ...formData,
                                    hasEndDate: v.target.checked
                                })} />}
                        />

                        <FormControlLabel
                            label={t("serviceModulesPage.calendarPage.eventHasTime")}
                            control={<Checkbox
                                checked={formData.hasTime}
                                onChange={(v) => setFormData({
                                    ...formData,
                                    hasTime: v.target.checked
                                })} />}
                        />
                    </Box>

                    <FormControlLabel
                        label={t("serviceModulesPage.calendarPage.eventUpdateNotifications")}
                        control={<Checkbox
                            checked={formData.sendUpdateNotification}
                            onChange={(v) => setFormData({
                                ...formData,
                                sendUpdateNotification: v.target.checked
                            })} />}
                    />
                </Box>
                <Box flex={1}>
                    <Typography variant="subtitle2">
                        {t("serviceModulesPage.calendarPage.eventReminderNotifications")}
                    </Typography>
                    <ReminderNotificationsSelector
                        hasTime={formData.hasTime}
                        reminderNotifications={formData.reminderNotifications}
                        onChange={(newReminders) => setFormData({
                            ...formData,
                            reminderNotifications: newReminders
                        })} />
                    <Box display="flex" flexDirection="column">
                        <FormControlLabel
                            label={assignmentLabel}
                            control={
                                <Checkbox
                                    checked={formData.isCompanyWideAssigned}
                                    onChange={(v) => setFormData({
                                        ...formData,
                                        isCompanyWideAssigned: v.target.checked
                                    })}
                                />
                            }
                        />

                        {
                            !formData.isCompanyWideAssigned &&
                            <SelectGroupsList
                                sx={{ ml: 4, mt: 1 }}
                                value={formData.assignedGroupIds}
                                onChange={(v) => setFormData({
                                    ...formData,
                                    assignedGroupIds: v
                                })}
                            />
                        }
                    </Box>
                    {
                        serviceModule.hasSurveyAppointmentSubtypes && <CalendarEventSurveyDetails
                            eventId={event.id!}
                            serviceModuleId={serviceModule.id}
                            formData={formData}
                            setFormData={(u) => setFormData(() => ({
                                ...formData,
                                ...u,
                            }))}
                        />
                    }
                </Box>
            </Stack>

            <DialogActions>
                <GeneralButton
                    appVariant="secondary"
                    onClick={handleClose}
                >
                    {t("buttons.cancel")}
                </GeneralButton>
                <GeneralButton
                    onClick={handleSave}
                    disabled={isSaveDisabled || false}
                >
                    {t("buttons.save")}
                </GeneralButton>

                {!isNewEvent && (
                    <>
                        <GeneralButton
                            appVariant="ghost"
                            onClick={() => onCopy?.(event as CalendarEvent)}
                        >
                            {t("buttons.copy")}
                        </GeneralButton>

                        <GeneralButton
                            appVariant="danger"
                            onClick={() => onDelete(event.id!)}
                        >
                            {t("buttons.delete")}
                        </GeneralButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CalendarEventModal;